import { t } from "@lingui/core/macro";
import type {
  CreateStripeAccount,
  GetAnalytics,
  GetPaymentDispute,
  GetPaymentForOverview,
  GetPaymentSettings,
  GetPaymentsList,
  GetStripeAccount,
  GetStripeAccountRefreshURL,
  GetTaxPayments,
  PostPaymentRefund,
  UpdateGlobalPaymentSettings,
  UpdateServicesPaymentSettings,
  PostSendStripeNotification,
  DeleteStripeAccount,
} from "@/server-types";
import { queryClient } from "..";
import { get, post, put, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { keepPreviousData } from "@tanstack/react-query";
import { addToast } from "@heroui/react";

/**
 * POST /stripe/payment/{intentId}/send-notification/{channel}
 */
export const usePostSendStripeNotification = () => {
  return useCustomMutation({
    mutationFn: ({
      intentId,
      channel,
    }: {
      intentId: string;
      channel: "email" | "fallbackFlow";
    }) =>
      post<PostSendStripeNotification>(
        `/stripe/payment/${intentId}/send-notification/${channel}`,
      ),
  });
};

/**
 * GET /stripe/account
 */
export const useGetStripeAccount = (enabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["getStripeAccount"],
    queryFn: () => get<GetStripeAccount>("/stripe/account"),
    staleTime: 300000,
    enabled: enabled ?? true,
  });
};

/**
 * POST /stripe/account
 */
export const useCreateStripeAccount = () => {
  return useCustomMutation({
    mutationFn: (body: CreateStripeAccount["body"]) =>
      post<CreateStripeAccount>("/stripe/account", body),
  });
};

/**
 * GET /stripe/paymentSettings
 */
export const useGetStripePaymentSettings = () => {
  return useCustomQuery({
    queryKey: ["getStripePaymentSettings"],
    queryFn: () => get<GetPaymentSettings>("/stripe/paymentSettings"),
  });
};

/**
 * PUT /stripe/paymentSettings/services
 */
export const useUpdateStripePaymentSettingsServices = () => {
  return useCustomMutation({
    mutationFn: (body: UpdateServicesPaymentSettings["body"]) =>
      put<UpdateServicesPaymentSettings>(
        "/stripe/paymentSettings/services",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getStripePaymentSettings"],
      });

      addToast({
        title: t`Nastavitve so bile uspešno posodobljene`,
        color: "success",
      });
    },
  });
};

/**
 * PUT /stripe/paymentSettings/global
 */
export const useUpdateStripePaymentSettingsGlobal = () => {
  return useCustomMutation({
    mutationFn: (body: UpdateGlobalPaymentSettings["body"]) =>
      put<UpdateGlobalPaymentSettings>("/stripe/paymentSettings/global", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getStripePaymentSettings"],
      });
    },
  });
};

/**
 * GET /stripe/account/refresh
 */
export const useGetStripeAccountOnboardingUrl = () => {
  return useCustomQuery({
    queryKey: ["getStripeAccountOnboardingUrl"],
    queryFn: () => get<GetStripeAccountRefreshURL>("/stripe/account/refresh"),
    enabled: false,
    refetchOnWindowFocus: false,
  });
};

/**
 * GET /stripe/analytics
 */
export const useGetStripeAnalytics = (query: GetAnalytics["query"]) => {
  return useCustomQuery({
    queryKey: ["getStripeAnalytics", query],
    queryFn: () => get<GetAnalytics>("/stripe/analytics", query),
  });
};

/**
 * GET /stripe/payments-overview
 */
export const useGetPaymentsList = (query: GetPaymentsList["query"]) => {
  return useCustomQuery({
    queryKey: ["getPaymentsList", query],
    queryFn: () => get<GetPaymentsList>("/stripe/payments-overview", query),
    placeholderData: keepPreviousData,
  });
};

/**
 * GET /stripe/payments-overview/{userAppointmentId}
 */
export const useGetPaymentForOverview = (
  userAppointmentId: number | undefined,
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["getPaymentForOverview", userAppointmentId],
    queryFn: () =>
      get<GetPaymentForOverview>(
        `/stripe/payments-overview/${userAppointmentId!}`,
      ),
    enabled: userAppointmentId != null && enabled,
  });
};

/**
 * GET /stripe/payments-overview/{userAppointmentId}/dispute
 */
export const useGetPaymentDispute = (
  userAppointmentId: number | undefined,
  enabled: boolean = true,
) => {
  return useCustomQuery({
    queryKey: ["getPaymentDispute", userAppointmentId],
    queryFn: () =>
      get<GetPaymentDispute>(
        `/stripe/payments-overview/${userAppointmentId!}/dispute`,
      ),
    enabled: userAppointmentId != null && enabled,
    staleTime: 300000,
    displayNotification: false,
  });
};

/**
 * GET /stripe/Tax/Export
 */
export const useGetTaxPayments = () => {
  return useCustomMutation({
    mutationFn: (query: GetTaxPayments["query"]) =>
      get<GetTaxPayments>("/stripe/tax/export", query),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      addToast({
        title: error.response?.data?.error ?? error?.message,
        color: "danger",
      });
    },
  });
};

/**
 * POST /stripe/payment/{intentId}/refund
 */
export const usePostPaymentRefund = () => {
  return useCustomMutation({
    mutationKey: ["postPaymentRefund"],
    mutationFn: ({
      intentId,
      body,
    }: {
      intentId: string;
      body: PostPaymentRefund["body"];
    }) => post<PostPaymentRefund>(`/stripe/payment/${intentId}/refund`, body),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      addToast({
        title: error.response?.data?.error ?? error?.message,
        color: "danger",
      });
    },
  });
};

export const useDeleteStripeAccount = () => {
  return useCustomMutation({
    mutationFn: () => del<DeleteStripeAccount>(`/stripe/account`),
  });
};
